'use client'

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/integrations/supabase/client'
import type { Json } from '@/integrations/supabase/types'
import { FollowUpOption, Conversation, Message, Category, MessageMetadata } from '@/types/conversation'
import { getGlobalMemoryPreloader } from '@/hooks/useMemoryPreloader'
import { QuizPayload } from '@/components/conversation/QuizModal'
import { emitPerformanceEvent } from '@/components/conversation/PerformanceDashboard'

/**
 * Clean AI response messages by stripping embedded JSON blocks,
 * memoryUpdates sections, and other raw structured data that
 * shouldn't be shown to users in the chat.
 */
function cleanAIResponseMessage(text: string): string {
  if (!text) return text
  let cleaned = text

  // Remove embedded JSON objects like {"memoryUpdates":{...}} or {"key":"value"}
  cleaned = cleaned.replace(/\{\s*"(?:memoryUpdates|memory_updates|nextStep|conversationComplete|followUpOptions|productSearch|isSelectable)"\s*:[\s\S]*?\}(?:\s*\})?/g, '')

  // Remove lines that look like raw JSON key-value pairs leaked from tool calls
  cleaned = cleaned.replace(/^\s*"(?:memoryUpdates|nextStep|conversationComplete|isSelectable)"\s*:.*$/gm, '')

  // Remove markdown code blocks containing JSON (often memory dumps)
  cleaned = cleaned.replace(/```(?:json)?\s*\{[\s\S]*?\}\s*```/g, '')

  // Remove "Memory Update:" or "Memory Updates:" sections
  cleaned = cleaned.replace(/(?:^|\n)\s*\*?\*?Memory\s*Updates?\*?\*?\s*:?[\s\S]*?(?=\n\n|$)/gi, '')

  // Remove natural language follow-up lists often added by models
  // Matches headers like "Would you like to:" or "Follow-up options:" followed by a list
  cleaned = cleaned.replace(/(?:\n|^)(?:(?:I can also help you with|Would you like to|Follow-up options?|Next steps?|Suggested questions?|Here are some follow-up options|You might also want to ask|Choose an option below|I suggest these follow-up options|Available follow-up options|Continue the conversation:?):?\s*\n?)(?:(?:\d+\.|[\-*]|\d+\))\s+.*\n?)+/gi, '')

  // Handle cases where options are listed at the very end without a specific header
  // (e.g. at least 2 numbered or bulleted lines at the end of the message)
  cleaned = cleaned.replace(/\n\n(?:(?:\d+\.|[\-*]|\d+\))\s+.*\n?){2,}$/g, '')

  // Clean up extra whitespace from removals
  cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim()

  return cleaned
}

type HookAIResponse = {
  message: string
  followUpOptions: FollowUpOption[]
  nextStep?: string
  conversationComplete: boolean
  memoryUpdates?: Record<string, unknown>
  affiliateLinks?: Array<{ name: string; url: string; description: string; vendor: string }>
  productResults?: Array<{ name: string; url: string; description: string; vendor: string; price?: string; rating?: string }>
  memoryVersion?: string
}

interface ConversationState {
  currentStep?: string
  completedSteps: string[]
  contextData: Record<string, unknown>
}

export interface EducationProgress {
  unit: number
  lesson: number
  masteryScore: number
  classId?: string | null
}

// Performance-optimized conversation hook with streaming and smart caching
export function useConversationOptimized(conversationId?: string) {
  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [followUpOptions, setFollowUpOptions] = useState<FollowUpOption[]>([])
  const latestFollowUpsRef = useRef<FollowUpOption[]>([])
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null)
  const setFollowUpsLive = useCallback((opts: FollowUpOption[]) => {
    latestFollowUpsRef.current = opts || []
    setFollowUpOptions(opts || [])
  }, [])
  const [productResults, setProductResults] = useState<Array<{ name: string; url: string; description: string; vendor: string; price?: string; rating?: string }>>([])
  const [quiz, setQuiz] = useState<QuizPayload | null>(null)
  const [isQuizOpen, setIsQuizOpen] = useState(false)
  const [conversationState, setConversationState] = useState<ConversationState>({
    completedSteps: [],
    contextData: {}
  })
  const [educationProgress, setEducationProgress] = useState<EducationProgress | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const latestIsStreamingRef = useRef(false)
  const setStreamingLive = useCallback((val: boolean) => {
    latestIsStreamingRef.current = val
    setIsStreaming(val)
  }, [])
  const [error, setError] = useState<string | null>(null)
  // --- TTS state ---
  const [isTtsEnabled, setIsTtsEnabled] = useState<boolean>(() => {
    try { return JSON.parse(sessionStorage.getItem('tts_enabled') || 'false') } catch { return false }
  })
  const [availableVoices, setAvailableVoices] = useState<SpeechSynthesisVoice[]>([])
  const [selectedVoice, setSelectedVoice] = useState<string>(() => {
    try { return localStorage.getItem('tts_voice_name') || '' } catch { return '' }
  })
  const [voiceSpeed, setVoiceSpeed] = useState<number>(() => {
    try { return parseFloat(localStorage.getItem('tts_speed') || '1.0') } catch { return 1.0 }
  })
  const lastSpokenMessageIdRef = useRef<string | null>(null)

  // Load available voices
  useEffect(() => {
    const loadVoices = () => {
      try {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) return
        const voices = window.speechSynthesis.getVoices()
        setAvailableVoices(voices)

        // If no voice is selected, try to find a good default
        if (!selectedVoice && voices.length > 0) {
          const defaultVoice = voices.find(v =>
            /female|woman|siri|google uk english female/i.test(v.name)
          ) || voices[0]
          setSelectedVoice(defaultVoice.name)
          try { localStorage.setItem('tts_voice_name', defaultVoice.name) } catch { }
        }
      } catch (e) {
        console.warn('[TTS] Failed to load voices', e)
      }
    }

    // Load voices immediately
    loadVoices()

    // Also load when voices change (Chrome loads asynchronously)
    if (typeof window !== 'undefined' && window.speechSynthesis.onvoiceschanged !== undefined) {
      window.speechSynthesis.onvoiceschanged = loadVoices
    }

    return () => {
      try {
        if (typeof window !== 'undefined' && window.speechSynthesis.onvoiceschanged) {
          window.speechSynthesis.onvoiceschanged = null
        }
      } catch { }
    }
  }, [selectedVoice])

  const speakText = useCallback((text: string) => {
    try {
      if (!isTtsEnabled || typeof window === 'undefined' || !('speechSynthesis' in window)) return
      window.speechSynthesis.cancel()
      const utterance = new SpeechSynthesisUtterance(text)

      // Set voice if available
      const voices = window.speechSynthesis.getVoices()
      const voice = voices.find(v => v.name === selectedVoice) || voices[0]
      if (voice) {
        utterance.voice = voice
      }

      utterance.rate = voiceSpeed
      utterance.pitch = 1
      window.speechSynthesis.speak(utterance)
    } catch (e) {
      console.warn('[TTS] speak failed', e)
    }
  }, [isTtsEnabled, selectedVoice, voiceSpeed])
  const stopSpeaking = useCallback(() => {
    try { if (typeof window !== 'undefined') window.speechSynthesis.cancel() } catch (_e) { }
  }, [])
  const toggleTts = useCallback(() => {
    setIsTtsEnabled(prev => {
      const next = !prev
      try { sessionStorage.setItem('tts_enabled', JSON.stringify(next)) } catch { }
      if (!next) { try { if (typeof window !== 'undefined') window.speechSynthesis.cancel() } catch (_e) { } }
      return next
    })
  }, [])
  const handleVoiceChange = useCallback((voiceName: string) => {
    setSelectedVoice(voiceName)
    try { localStorage.setItem('tts_voice_name', voiceName) } catch { }
  }, [])
  const handleVoiceSpeedChange = useCallback((speed: number) => {
    setVoiceSpeed(speed)
    try { localStorage.setItem('tts_speed', speed.toString()) } catch { }
  }, [])

  // Cache user context to avoid repeated DB calls
  interface CachedUserContext {
    id: string
    name?: string
    age?: number
    age_bracket?: string
    gender?: string
    country_code?: string
    preferred_persona?: string
    preferences?: Record<string, unknown>
  }
  const [userContext, setUserContext] = useState<CachedUserContext | null>(null)

  const { i18n } = useTranslation()

  // Memoized conversation data to prevent unnecessary re-renders
  const memoizedConversation = useMemo(() => conversation, [conversation])

  // 🔹 Fetch conversation and state from DB
  // Separate function to refresh messages only (for voice->text switch)
  const refreshMessages = useCallback(async () => {
    // CRITICAL FIX: Use the state ID if the prop ID is undefined (for CategoryPage)
    const activeId = conversationId || memoizedConversation?.id

    if (!activeId) {
      console.log('[CONVERSATION] ⚠️ No active ID for refreshMessages')
      return
    }

    console.log('[CONVERSATION] 🔄 Refreshing messages from database for conversation:', activeId)
    try {
      const { data: msgsResult, error: msgsError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', activeId)
        .order('created_at', { ascending: true })
        .limit(200)

      if (msgsError) throw msgsError

      const newMsgs = (msgsResult || []) as Message[]

      setMessages(prev => {
        // If we are currently streaming an assistant message, preserve it in the state
        // even if it's not in the database yet.
        const isCurrentlyStreaming = prev.some(m => m.role === 'assistant' && (m as any).isStreaming)
        if (isCurrentlyStreaming) {
          const streamingMsg = prev.find(m => m.role === 'assistant' && (m as any).isStreaming)
          if (streamingMsg) {
            // Find if this message ID is already in the DB result (unlikely while still streaming)
            const existsInDb = newMsgs.some(m => m.id === streamingMsg.id)
            if (!existsInDb) {
              return [...newMsgs, streamingMsg]
            }
          }
        }
        return newMsgs
      })
      console.log(`[CONVERSATION] ✅ Refreshed ${msgsResult?.length || 0} messages`)
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.message?.includes('AbortError')) {
        console.log('[CONVERSATION] Refresh messages aborted')
      } else {
        console.error('[CONVERSATION] ❌ Failed to refresh messages:', err)
      }
    }
  }, [conversationId, memoizedConversation?.id])

  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchConversation = useCallback(async () => {
    if (!conversationId) return
    const startTime = performance.now()

    // Abort previous fetch
    if (abortControllerRef.current) abortControllerRef.current.abort()
    abortControllerRef.current = new AbortController()
    const signal = abortControllerRef.current.signal

    try {
      setIsLoading(true)

      let convQuery = supabase.from('conversations').select(`*, category:categories(*)`).eq('id', conversationId)
      let msgsQuery = supabase.from('messages').select('*').eq('conversation_id', conversationId).order('created_at', { ascending: true }).limit(200)
      let stateQuery = supabase.from('conversation_states').select('*').eq('conversation_id', conversationId)

      if (signal) {
        convQuery = convQuery.abortSignal(signal)
        msgsQuery = msgsQuery.abortSignal(signal)
        stateQuery = stateQuery.abortSignal(signal)
      }

      const [convResult, msgsResult, stateResult] = await Promise.all([
        convQuery.single(),
        msgsQuery,
        stateQuery.maybeSingle()
      ])

      const dbMs = performance.now() - startTime
      console.log(`[PERF] Conversation fetch took ${dbMs.toFixed(0)}ms`)
      emitPerformanceEvent('db_query_complete', { duration: dbMs })

      if (convResult.error) throw convResult.error
      setConversation(convResult.data as unknown as Conversation)

      if (msgsResult.error) throw msgsResult.error
      setMessages((msgsResult.data || []) as Message[])

      if (stateResult.data) {
        setConversationState({
          currentStep: stateResult.data.current_step || undefined,
          completedSteps: (stateResult.data.completed_steps as string[]) || [],
          contextData: (stateResult.data.context_data as Record<string, unknown>) || {}
        })

        // Fetch education progress if relevant
        const isEdu = (convResult.data as any)?.category?.is_educational
        if (isEdu) {
          const { data: progData } = await supabase
            .from('student_subject_progress')
            .select('*')
            .eq('user_id', (convResult.data as any).user_id)
            .eq('category_id', (convResult.data as any).category_id)
            .maybeSingle()

          if (progData) {
            setEducationProgress({
              unit: progData.current_unit || stateResult.data.edu_lesson_unit || 1,
              lesson: progData.current_lesson || stateResult.data.edu_lesson_number || 1,
              masteryScore: progData.mastery_score || 0,
              classId: stateResult.data.edu_class_id
            })
          } else {
            setEducationProgress({
              unit: stateResult.data.edu_lesson_unit || 1,
              lesson: stateResult.data.edu_lesson_number || 1,
              masteryScore: 0,
              classId: stateResult.data.edu_class_id
            })
          }
        }
      }
    } catch (err: any) {
      if (err?.name === 'AbortError' || err?.message?.includes('AbortError')) {
        console.log('[CONVERSATION] Fetch aborted')
      } else {
        console.error('Error fetching conversation:', err)
        setError('Failed to load conversation')
      }
    } finally {
      setIsLoading(false)
    }
  }, [conversationId])

  // 🔹 Preload user context (profile)
  const preloadUserContext = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) return

      const { data: userProfile } = await supabase
        .from('users')
        .select('id, name, age, age_bracket, gender, country_code, preferred_persona, preferences')
        .eq('id', user.id)
        .single()

      if (userProfile) {
        setUserContext({
          id: userProfile.id,
          name: userProfile.name ?? undefined,
          age: userProfile.age ?? undefined,
          age_bracket: userProfile.age_bracket ?? undefined,
          gender: userProfile.gender ?? undefined,
          country_code: userProfile.country_code ?? undefined,
          preferred_persona: userProfile.preferred_persona ?? undefined,
          preferences: (userProfile.preferences as unknown as Record<string, unknown>) ?? undefined
        })
        sessionStorage.setItem('userContext', JSON.stringify(userProfile))
      }
    } catch (err) {
      console.error('Error preloading user context:', err)
    }
  }, [])

  useEffect(() => {
    const abortController = new AbortController()

    const cached = sessionStorage.getItem('userContext')
    if (cached) {
      setUserContext(JSON.parse(cached) as CachedUserContext)
    } else {
      preloadUserContext()
    }

    return () => {
      abortController.abort()
    }
  }, [preloadUserContext])

  useEffect(() => {
    let isMounted = true

    const loadConversation = async () => {
      if (conversationId && isMounted) {
        await fetchConversation()
      }
    }

    loadConversation()

    return () => {
      isMounted = false
    }
  }, [conversationId, fetchConversation])

  // 🔄 Realtime subscription for voice messages
  useEffect(() => {
    const currentConversationId = conversationId || conversation?.id
    if (!currentConversationId) return

    console.log('[CONVERSATION] 🔄 Setting up realtime subscription for voice messages...')

    const channel = supabase
      .channel(`messages-${currentConversationId}`)
      .on('postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${currentConversationId}`
        },
        (payload) => {
          console.log('[CONVERSATION] 📨 New message received via realtime:', payload.new)

          // CRITICAL: Avoid infinite loops and state wiping during active streaming
          // If we are currently streaming a message locally, we don't want to refresh
          // everything from the DB because the streaming message isn't in DB yet.
          if (latestIsStreamingRef.current) {
            console.log('[CONVERSATION] ⏳ Stream in progress, delaying realtime refresh')
            return
          }

          // Check if it's a voice message
          const isVoiceMessage = payload.new?.is_voice_message || payload.new?.metadata?.isVoice
          console.log('[CONVERSATION] 📨 Message details:', {
            role: payload.new?.role,
            isVoiceMessage,
            content: payload.new?.content?.substring(0, 50) + '...',
          })

          // Only refresh if it's NOT a message we just inserted ourselves (role check is basic)
          // We can't easily check 'own' status without tracking local IDs, but we can check if streaming.
          console.log('[CONVERSATION] 🔄 Refreshing messages to include new message...')
          refreshMessages()
        }
      )
      .subscribe()

    // Fallback: Listen for custom voice message events
    const handleVoiceMessageSaved = (event: CustomEvent) => {
      const { conversationId: eventConversationId, role, content } = event.detail
      if (eventConversationId === currentConversationId) {
        console.log('[CONVERSATION] 📨 Voice message fallback triggered for', role, 'message:', content?.substring(0, 50) + '...')
        refreshMessages()
      }
    }

    window.addEventListener('voiceMessageSaved', handleVoiceMessageSaved as EventListener)

    return () => {
      console.log('[CONVERSATION] 🧹 Cleaning up realtime subscription')
      supabase.removeChannel(channel)
      window.removeEventListener('voiceMessageSaved', handleVoiceMessageSaved as EventListener)
    }
  }, [conversationId, conversation?.id, refreshMessages])

  // 🔊 TTS: Speak the latest assistant message when fully received (non-streaming)
  useEffect(() => {
    if (!isTtsEnabled) return
    if (messages.length === 0 || isLoading || isStreaming) return
    const last = messages[messages.length - 1]
    if (last.role !== 'assistant' || !last.content) return
    if (lastSpokenMessageIdRef.current === last.id) return
    lastSpokenMessageIdRef.current = last.id
    speakText(last.content)
    return () => { stopSpeaking() }
  }, [messages, isLoading, isStreaming, isTtsEnabled, speakText, stopSpeaking])

  // 🔹 Streaming handler (SSE) - FIXED: Process all chunks including after [DONE]
  const handleStreamingResponse = async (response: Response) => {
    console.log('[DEBUG] Starting streaming response handler')
    if (!response.body) {
      console.error('[DEBUG] No response body for streaming')
      return
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let accumulatedContent = ''
    let timeoutId: NodeJS.Timeout | null = null

    setIsLoading(true)
    setError(null)
    setStreamingLive(true)
    const streamStart = performance.now()
    console.log('[DEBUG] Stream reader created, starting to read chunks')

    // Set up timeout to prevent hanging
    const setupTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(() => {
        console.warn('[DEBUG] Streaming timeout after 30 seconds, forcing completion')
        setStreamingLive(false)
        reader.cancel()
      }, 30000) // 30 second timeout
    }

    setupTimeout()

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) {
          console.log('[DEBUG] Stream reading done, processing remaining buffer')
          if (timeoutId) clearTimeout(timeoutId)
          // Process any remaining buffer before exiting
          if (buffer.trim()) {
            const remainingLines = buffer.split('\n')
            for (const line of remainingLines) {
              if (line.startsWith('data: ') && line !== 'data: [DONE]') {
                try {
                  const data = JSON.parse(line.slice(6))
                  if (data.followUpOptions) {
                    console.log('[DEBUG] ✅ FINAL: Setting follow-up options from remaining buffer:', data.followUpOptions)
                    setFollowUpsLive(data.followUpOptions)
                  }
                  if (data.productResults) {
                    console.log('[DEBUG] ✅ FINAL: Setting product results from remaining buffer:', data.productResults.length)
                    setProductResults(data.productResults)
                  }
                } catch {
                  console.warn('[DEBUG] Failed to parse final buffer line:', line)
                }
              }
            }
          }
          break
        }

        // Reset timeout on each successful read
        setupTimeout()

        const chunk = decoder.decode(value, { stream: true })
        console.log('[DEBUG] Received chunk:', chunk.slice(0, 100) + (chunk.length > 100 ? '...' : ''))
        buffer += chunk

        // Split into lines but keep processing ALL lines
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          // Check for [DONE] marker
          if (line === 'data: [DONE]') {
            console.log('[DEBUG] Received [DONE] marker, but continuing to process buffer')
            continue
          }

          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6))
              console.log('[DEBUG] Parsed streaming data:', Object.keys(data))

              // Content streaming
              if (data.choices?.[0]?.delta?.content) {
                const newContent = data.choices[0].delta.content
                accumulatedContent += newContent

                // Clean content ON THE FLY to avoid showing duplicate follow-up options or JSON noise
                const cleanedContent = cleanAIResponseMessage(accumulatedContent)

                setMessages(prev => {
                  const lastMessage = prev[prev.length - 1]
                  if (lastMessage && lastMessage.role === 'assistant') {
                    return [
                      ...prev.slice(0, -1),
                      { ...lastMessage, content: cleanedContent, isStreaming: true } as any
                    ]
                  }
                  return prev
                })
              }

              // CRITICAL: Handle follow-up options (these come BEFORE [DONE])
              if (data.followUpOptions) {
                console.log('[DEBUG] ✅ Setting follow-up options from stream:', data.followUpOptions.length, 'options')
                setFollowUpsLive(data.followUpOptions)
                // Attach follow-ups to the last assistant message's metadata so UI can render from message
                setMessages(prev => {
                  const lastMessage = prev[prev.length - 1]
                  if (lastMessage && lastMessage.role === 'assistant') {
                    const updatedMessage = {
                      ...lastMessage,
                      metadata: { ...(lastMessage.metadata || {}), followUpOptions: data.followUpOptions }
                    } as Message
                    return [...prev.slice(0, -1), updatedMessage]
                  }
                  return prev
                })
              }

              // Handle product results
              if (data.productResults) {
                console.log('[DEBUG] ✅ Setting product results from stream:', data.productResults.length, 'products')
                setProductResults(data.productResults)
              }

              // Handle quiz payload from stream
              if (data.quiz) {
                try {
                  setQuiz(data.quiz as QuizPayload)
                  setIsQuizOpen(true)
                } catch {
                  console.warn('[DEBUG] Failed to set quiz from stream payload')
                }
              }
            } catch {
              console.warn('[DEBUG] Failed to parse streaming data. Line:', line.slice(0, 100))
            }
          }
        }
      }
    } catch (error) {
      console.error('[DEBUG] Streaming error:', error)
      // Even on error, try to save what we have
      if (accumulatedContent.trim().length > 0) {
        console.log('[DEBUG] Saving partial response due to error')
        const convId = memoizedConversation?.id
        if (convId) {
          try {
            await supabase.from('messages').insert([
              {
                conversation_id: convId,
                role: 'assistant',
                content: accumulatedContent + '\n\n[Response was interrupted due to a technical issue]',
                metadata: ({ followUpOptions: latestFollowUpsRef.current, error: true } as unknown as Json)
              }
            ])
          } catch (e) {
            console.warn('[DEBUG] Failed to persist partial response:', e)
          }
        }
      }
    } finally {
      if (timeoutId) clearTimeout(timeoutId)
      setStreamingLive(false)
      emitPerformanceEvent('streaming_complete', { duration: performance.now() - streamStart })

      // Persist assistant message to DB
      try {
        const convId = memoizedConversation?.id
        if (convId && accumulatedContent.trim().length > 0) {
          const finalCleaned = cleanAIResponseMessage(accumulatedContent)
          await supabase.from('messages').insert([
            {
              conversation_id: convId,
              role: 'assistant',
              content: finalCleaned,
              metadata: ({ followUpOptions: latestFollowUpsRef.current } as unknown as Json)
            }
          ])
          console.log('[DEBUG] ✅ Streamed response saved to database')
        }
      } catch (e) {
        console.warn('[DEBUG] Failed to persist streamed assistant message:', e)
      }
    }
  }

  // 🔹 Submit quiz results: persist summary to messages and update memories
  const submitQuizResults = useCallback(async (result: { quizId: string; lessonId?: string; score: number; total: number; perQuestion: Array<{ questionId: string; correct: boolean; userAnswers: number[] }>; weakObjectives?: string[]; answers: number[][] }) => {
    try {
      const convId = memoizedConversation?.id
      if (!convId) return
      // Send complete structured payload to backend for scoring + chat response
      await sendMessage('Submit quiz', false, [], {
        action: 'submit_quiz',
        quizId: result.quizId,
        lessonId: result.lessonId,
        score: result.score,
        total: result.total,
        perQuestion: result.perQuestion,
        weakObjectives: result.weakObjectives || [],
        answers: result.answers
      })
    } finally {
      setIsQuizOpen(false)
    }
  }, [memoizedConversation?.id])

  // 🔹 Send message to AI
  const sendMessage = async (content: string, useStream = true, imageDataUrls?: string[], structuredData?: Record<string, unknown>, displayContent?: string) => {
    if (isLoading) return
    const currentConversation = memoizedConversation
    if (!currentConversation) return

    const startTime = performance.now()
    emitPerformanceEvent('conversation_start', { timestamp: startTime })
    setIsLoading(true)
    setError(null)

    // Optimistic message (use displayContent if provided, for clean UI)
    const optimisticUserMessage: Message = {
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      role: 'user',
      content: displayContent || content, // Use clean display content for history
      conversation_id: currentConversation.id,
      metadata: (structuredData ? { structuredPayload: structuredData } : {}) as MessageMetadata
    }
    setMessages(prev => [...prev, optimisticUserMessage])

    // Persist user message to DB for history
    try {
      const { data: insertedUserMsg } = await supabase
        .from('messages')
        .insert({
          conversation_id: currentConversation.id,
          role: 'user',
          content: displayContent || content, // Save cleaned text to DB
          metadata: (structuredData ? { structuredPayload: structuredData } : {}) as Json
        })
        .select()
        .single()
      if (insertedUserMsg) {
        setMessages(prev => prev.map(m => m.id === optimisticUserMessage.id ? (insertedUserMsg as Message) : m))
      }
    } catch (e) {
      console.warn('Failed to persist user message (will still continue):', e)
    }

    if (useStream) {
      const placeholderAiMessage: Message = {
        id: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        role: 'assistant',
        content: '',
        conversation_id: currentConversation.id,
        metadata: {}
      }
      setMessages(prev => [...prev, placeholderAiMessage])
    }

    try {
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) throw new Error('User not authenticated')

      const preloader = getGlobalMemoryPreloader()
      const categoryName = (currentConversation.category as Category)?.name || 'general'

      // Ensure we have valid preloader data, fallback to userContext if needed
      const cachedProfile = preloader?.userProfile || userContext
      const cachedCategoryMemory = preloader?.getMemoryForCategory?.(categoryName) || {}
      const memoryVersion = preloader?.getMemoryVersionForCategory?.(categoryName) || ''

      // If preloader is not available, try to initialize it
      if (!preloader && userContext) {
        console.warn('[PERF] Global memory preloader not available, using fallback userContext')
      }

      // Smart streaming detection and fallback
      let shouldStream = useStream

      const requestBody = {
        conversationId: currentConversation.id,
        userResponse: content,
        category: categoryName,
        conversationHistory: [...messages, optimisticUserMessage].slice(-3).map(msg => ({ role: msg.role, content: msg.content })),
        currentStep: conversationState.currentStep,
        userId: user.id,
        locale: i18n.language,
        stream: shouldStream,
        userContext: cachedProfile,
        userMemory: cachedCategoryMemory,
        memoryVersion,
        attachments: imageDataUrls && imageDataUrls.length ? imageDataUrls : undefined,
        structuredPayload: structuredData // Include structured data if available
      }

      console.log('[DEBUG] Request body:', requestBody)
      if (shouldStream) {
        console.log('[DEBUG] Attempting streaming request')
        const { data: { session } } = await supabase.auth.getSession()
        const user = session?.user
        let accessToken: string | undefined;
        if (user) {
          accessToken = session?.access_token

          if (!accessToken) {
            console.error('[AUTH] No access token available, cannot proceed with AI conversation')
            setError('Authentication required. Please refresh the page.')
            setMessages(prev => prev.filter(m => m.id !== optimisticUserMessage.id))
            setIsLoading(false)
            setStreamingLive(false)
            return
          }
        } else {
          console.error('[AUTH] User not authenticated for streaming, cannot proceed with AI conversation')
          setError('Authentication required. Please refresh the page.')
          setMessages(prev => prev.filter(m => m.id !== optimisticUserMessage.id))
          setIsLoading(false)
          setStreamingLive(false)
          return
        }

        const supabaseUrl = "https://lpwjdezzaylhiuyyugqt.supabase.co"
        console.log('[DEBUG] Streaming URL:', supabaseUrl)

        const response = await fetch(`${supabaseUrl}/functions/v1/ai-conversation`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
            'Accept': 'text/event-stream'
          },
          body: JSON.stringify(requestBody)
        })

        console.log('[DEBUG] Streaming response status:', response.status)
        console.log('[DEBUG] Streaming response headers:', Object.fromEntries(response.headers.entries()))

        // Check if server is actually streaming
        const contentType = response.headers.get('content-type') || ''
        const isStreaming = response.ok && contentType.includes('text/event-stream')

        if (isStreaming && response.ok) {
          console.log('[DEBUG] Server is streaming, handling SSE response')
          await handleStreamingResponse(response)
          emitPerformanceEvent('total_response_complete', { duration: performance.now() - startTime })
          return // Exit early on successful streaming
        } else {
          // Handle auth errors or non-streaming response
          if (response.status === 401 || response.status === 403) {
            console.log('[DEBUG] Auth error on streaming, falling back to functions.invoke')
          } else {
            console.log('[DEBUG] Server not streaming (content-type:', contentType, '), falling back to non-streaming')
          }
          shouldStream = false
          // Remove the placeholder AI message since streaming failed
          setMessages(prev => prev.filter(m => !(m.role === 'assistant' && m.content === '')))
        }
      }

      // Non-streaming fallback
      if (!shouldStream) {
        console.log('[DEBUG] Using non-streaming fallback')
        const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-conversation', {
          body: requestBody
        })

        if (aiError) {
          console.error('[DEBUG] AI function error:', aiError)
          // Handle auth errors specifically
          if (aiError.message?.includes('401') || aiError.message?.includes('403')) {
            setError('Your session has expired. Please sign in again.')
            setMessages(prev => prev.filter(m => m.id !== optimisticUserMessage.id))
            // Force session refresh and advise UI to retry
            await supabase.auth.getSession()
            return
          }
          throw aiError
        }

        console.log('[DEBUG] AI response data:', aiData)
        const aiResponse = aiData as HookAIResponse
        const rawMessage = aiResponse.message || 'I apologize, but I encountered an issue processing your request.'
        const messageContent = cleanAIResponseMessage(rawMessage)
        console.log('[DEBUG] Message content (cleaned):', messageContent)

        // Persist assistant message to DB then update UI
        let insertedAssistant: Message | null = null
        try {
          const { data: insertedAi } = await supabase
            .from('messages')
            .insert([
              {
                conversation_id: requestBody.conversationId,
                role: 'assistant',
                content: messageContent,
                metadata: ({ followUpOptions: aiResponse.followUpOptions || [], memoryVersion: aiResponse.memoryVersion || '' } as unknown as Json)
              }
            ])
            .select()
            .single()
          insertedAssistant = insertedAi as unknown as Message
        } catch (e) {
          console.warn('Failed to persist assistant message, falling back to in-memory only:', e)
        }

        const newAiMessage: Message = insertedAssistant || {
          id: crypto.randomUUID(),
          created_at: new Date().toISOString(),
          role: 'assistant',
          content: messageContent,
          conversation_id: requestBody.conversationId,
          metadata: {
            followUpOptions: aiResponse.followUpOptions || [],
            memoryVersion: aiResponse.memoryVersion || ''
          }
        }
        // Replace any prior assistant messages that still carry follow-up buttons
        setMessages(prev => {
          const filtered = prev.filter(m => {
            if (m.role !== 'assistant') return true
            if (m.content === '') return false
            const meta = m.metadata as unknown as { followUpOptions?: unknown[] } | undefined
            return !(meta && Array.isArray(meta.followUpOptions) && meta.followUpOptions.length > 0)
          })
          return [...filtered, newAiMessage]
        })

        // Always surface contextual follow-ups from the AI for continuous conversation
        setFollowUpOptions(aiResponse.followUpOptions || [])
        setProductResults(aiResponse.productResults || [])

        // Handle quiz in non-stream path
        const anyResp = aiData as unknown as { quiz?: QuizPayload }
        if (anyResp && anyResp.quiz) {
          setQuiz(anyResp.quiz as QuizPayload)
          setIsQuizOpen(true)
        }

        // Update memory cache if version changed
        if (aiResponse.memoryVersion && preloader) {
          const currentVersion = preloader.getMemoryVersionForCategory?.(categoryName)
          if (currentVersion !== aiResponse.memoryVersion) {
            console.log(`[CACHE] Memory version updated for ${categoryName}: ${currentVersion} → ${aiResponse.memoryVersion}`)
            // Trigger cache refresh for this category
            preloader.preloadUserData?.(true)
          }
        }

        const newState = {
          currentStep: aiResponse.nextStep,
          completedSteps: aiResponse.nextStep && aiResponse.nextStep !== conversationState.currentStep
            ? [...new Set([...conversationState.completedSteps, conversationState.currentStep])].filter(Boolean) as string[]
            : conversationState.completedSteps,
          contextData: { ...conversationState.contextData, ...(aiResponse.memoryUpdates || {}) }
        }
        setConversationState(newState)
        emitPerformanceEvent('total_response_complete', { duration: performance.now() - startTime })
      }
    } catch (err) {
      console.error('Error sending message:', err)
      setError('Failed to send message')
      setMessages(prev => prev.filter(m => m.id !== optimisticUserMessage.id))
    } finally {
      setIsLoading(false)
    }
  }

  // 🔹 Kickoff with AI
  const kickoffWithAI = useCallback(async (newConversation: Conversation, category: Category) => {
    setIsLoading(true)
    try {
      // Ensure we have a valid session before proceeding
      const { data: { session } } = await supabase.auth.getSession()
      const user = session?.user
      if (!user) throw new Error('User not authenticated')

      // Small delay to ensure conversation is committed to database before AI check
      await new Promise(resolve => setTimeout(resolve, 100))

      void supabase.functions.invoke('ai-conversation', { body: { warmup: true } }).catch(() => { })

      let retryCount = 0
      let aiData = null
      let aiError = null

      // Try up to 2 times in case of auth issues
      while (retryCount < 2) {
        const result = await supabase.functions.invoke('ai-conversation', {
          body: {
            conversationId: newConversation.id,
            userResponse: 'Start the conversation.',
            category: category.name,
            conversationHistory: [],
            userId: user.id,
            locale: i18n.language
          }
        })

        aiData = result.data
        aiError = result.error

        // If auth error and first try, refresh session and retry
        if ((result.error?.message?.includes('401') || result.error?.message?.includes('403')) && retryCount === 0) {
          console.log('[AUTH] Refreshing session and retrying...')
          await supabase.auth.getSession()
          retryCount++
          continue
        }

        break
      }

      if (aiError) {
        if (aiError.message?.includes('401') || aiError.message?.includes('403')) {
          throw new Error('Please sign in to start a conversation.')
        }
        throw aiError
      }

      const aiResponse = aiData as HookAIResponse
      const rawInitialMessage = aiResponse.message || 'Hello! I\'m here to help you. How can I assist you today?'
      const initialMessageContent = cleanAIResponseMessage(rawInitialMessage)

      const metadata: Json = { followUpOptions: (aiResponse.followUpOptions || []) as unknown as Json, isInitial: true }
      const { data: aiMessage, error: aiMessageError } = await supabase.from('messages').insert({
        conversation_id: newConversation.id,
        role: 'assistant' as const,
        content: initialMessageContent,
        metadata
      }).select().single()

      if (aiMessageError) throw aiMessageError

      setMessages([aiMessage as Message])
      setFollowUpOptions(aiResponse.followUpOptions || [])

      setConversationState({
        currentStep: aiResponse.nextStep,
        completedSteps: [],
        contextData: aiResponse.memoryUpdates || {}
      })
    } catch (err) {
      console.error('AI kickoff failed:', err)
      setError("I'm sorry, I'm having trouble starting our conversation. Please try again in a moment.")
    } finally {
      setIsLoading(false)
    }
  }, [i18n.language])



  // 🔹 Start a new conversation with gating
  const startNewConversation = useCallback(async (categoryId: string, userId: string) => {
    setIsLoading(true)
    try {
      // Check conversation limits first
      const { data: limitCheck, error: limitError } = await supabase
        .rpc('can_start_conversation', { p_user_id: userId })

      if (limitError) {
        console.error('Error checking conversation limits:', limitError)
        // Continue anyway on error to not block users
      } else if (limitCheck && typeof limitCheck === 'object' && 'can_create' in limitCheck && !limitCheck.can_create) {
        const limitData = limitCheck as unknown as {
          can_create: boolean
          current_count: number
          max_allowed: number
          is_unlimited: boolean
        }
        // Return special error with limit info
        setError('CONVERSATION_LIMIT_REACHED')
        return {
          error: 'CONVERSATION_LIMIT_REACHED',
          limitInfo: {
            currentCount: limitData.current_count,
            maxAllowed: limitData.max_allowed
          }
        }
      }

      const { data: category, error: categoryError } = await supabase.from('categories').select('*').eq('id', categoryId).single()
      if (categoryError) throw categoryError

      const { data: newConversation, error: convError } = await supabase.from('conversations').insert({
        user_id: userId,
        category_id: categoryId,
        title: `${category.name} Conversation`
      }).select('*, category:categories(*)').single()

      if (convError) throw convError

      setConversation(newConversation as unknown as Conversation)
      setMessages([])
      setFollowUpOptions([])
      setConversationState({ completedSteps: [], contextData: {} })

      // Do not await kickoffWithAI to speed up perceived start time.
      // The AI response will be delivered to the new page via realtime subscription.
      kickoffWithAI(newConversation as unknown as Conversation, category as unknown as Category).catch(err => console.error('[CONVERSATION] Kickoff failed:', err))
      return newConversation
    } catch (err) {
      console.error('Error starting conversation:', err)
      setError('Failed to start a new conversation')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [kickoffWithAI])

  return {
    conversation: memoizedConversation,
    messages,
    followUpOptions,
    productResults,
    isLoading,
    isStreaming,
    error,
    sendMessage,
    startNewConversation,
    refreshMessages, // For reloading messages after voice->text switch
    userContext,
    // TTS controls
    isTtsEnabled,
    toggleTts,
    availableVoices,
    selectedVoice,
    handleVoiceChange,
    voiceSpeed,
    handleVoiceSpeedChange,
    // Quiz API
    quiz,
    isQuizOpen,
    setIsQuizOpen,
    submitQuizResults,
    educationProgress
  }
}
